/**
 * Odda Facets — Self-contained filtering/sorting JS
 * Based on Dawn's facets.js, adapted for the Odda collection template.
 * No dependencies on Dawn's global.js or August theme JS.
 */

class OddaFacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.debouncedOnSubmit = this.debounce(this.onSubmitHandler.bind(this), 500);
    this.cache = {};
    this.sectionId = this.dataset.sectionId;
  }

  get form() {
    return this.querySelector('form');
  }

  connectedCallback() {
    // Checkbox / radio — fire immediately on change
    this.addEventListener('change', (e) => {
      const fromDrawer = !!e.target.closest('.odda-facets__drawer');
      if (e.target.type === 'checkbox' || e.target.type === 'radio') {
        this.applyFilters(fromDrawer);
      } else if (e.target.matches('[data-odda-sort-select]')) {
        this.applyFilters(false);
      } else {
        // Price inputs etc — debounce
        this.debouncedOnSubmit(e);
      }
    });

    document.addEventListener('click', this.onPaginationClick.bind(this));

    window.addEventListener('popstate', this.onHistoryChange.bind(this));
  }

  onPaginationClick(event) {
    const link = event.target.closest('#odda-pagination a');
    if (!link) return;
    event.preventDefault();
    this.fetchAndRender(link.href).then(() => {
      const target = document.querySelector('.odda-facets__toolbar') || document.querySelector('#odda-product-grid');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  debounce(fn, wait) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  onSubmitHandler(event) {
    this.applyFilters(false);
  }

  applyFilters(fromDrawer = false) {
    const form = this.form;
    if (!form) return;

    const searchParams = new URLSearchParams();

    form.querySelectorAll('input, select').forEach((input) => {
      const isInDrawer = !!input.closest('.odda-facets__drawer');

      // Only read filter inputs from the active context (drawer or main)
      // Sort select is always outside the drawer, so always include it
      if (!input.matches('[data-odda-sort-select]')) {
        if (fromDrawer && !isInDrawer) return;
        if (!fromDrawer && isInDrawer) return;
      }

      if (input.type === 'checkbox') {
        if (input.checked) {
          searchParams.append(input.name, input.value);
        }
        return;
      }

      if (input.matches('[data-odda-sort-select]')) {
        if (input.value) searchParams.set('sort_by', input.value);
        return;
      }

      // Price range and other inputs
      if (input.value && input.value !== '') {
        searchParams.append(input.name, input.value);
      }
    });

    const url = `${window.location.pathname}?${searchParams.toString()}`;
    this.fetchAndRender(url);
  }

  onHistoryChange(event) {
    const url = window.location.href;
    this.fetchAndRender(url, false);
  }

  async fetchAndRender(url, pushState = true) {
    this.showLoading();

    const sectionUrl = this.buildSectionUrl(url);

    try {
      let html;
      if (this.cache[sectionUrl]) {
        html = this.cache[sectionUrl];
      } else {
        const response = await fetch(sectionUrl);
        html = await response.text();
        this.cache[sectionUrl] = html;
      }

      this.renderSectionFromFetch(html);

      if (pushState) {
        history.pushState({}, '', url);
      }
    } catch (error) {
      console.error('Odda facets: Error fetching filtered results', error);
    } finally {
      this.hideLoading();
    }
  }

  buildSectionUrl(url) {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('section_id', this.sectionId);
    return urlObj.toString();
  }

  renderSectionFromFetch(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Update product grid
    this.updateElement(doc, '#odda-product-grid');
    // Update product count
    this.updateElement(doc, '#odda-product-count');
    // Update active filters
    this.updateElement(doc, '#odda-active-facets');
    // Update filter form inputs (sidebar)
    this.updateElement(doc, '#odda-facets-sidebar');
    // Update filter form inputs (drawer)
    this.updateElement(doc, '#odda-facets-drawer-filters');
    // Update pagination
    this.updateElement(doc, '#odda-pagination');
  }

  updateElement(doc, selector) {
    const newEl = doc.querySelector(selector);
    const currentEl = document.querySelector(selector);
    if (newEl && currentEl) {
      currentEl.innerHTML = newEl.innerHTML;
    }
  }

  showLoading() {
    const overlay = document.querySelector('.odda-loading-overlay');
    if (overlay) overlay.classList.add('is-active');
  }

  hideLoading() {
    const overlay = document.querySelector('.odda-loading-overlay');
    if (overlay) overlay.classList.remove('is-active');
  }
}

class OddaFacetRemove extends HTMLElement {
  connectedCallback() {
    this.querySelector('a, button')?.addEventListener('click', (e) => {
      e.preventDefault();
      const url = this.dataset.href || this.querySelector('a')?.getAttribute('href');
      if (url) {
        const form = document.querySelector('odda-facet-filters-form');
        if (form) {
          form.fetchAndRender(url);
        }
      }
    });
  }
}

class OddaPriceRange extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('input').forEach((input) => {
      input.addEventListener('change', this.onRangeChange.bind(this));
    });
  }

  onRangeChange(event) {
    this.adjustRange(event);
    const form = this.closest('odda-facet-filters-form');
    if (form) {
      form.applyFilters();
    }
  }

  adjustRange(event) {
    const minInput = this.querySelector('input[data-odda-price-min]');
    const maxInput = this.querySelector('input[data-odda-price-max]');

    if (!minInput || !maxInput) return;

    const min = parseFloat(minInput.value) || 0;
    const max = parseFloat(maxInput.value) || Infinity;

    if (min > max && maxInput.value !== '') {
      event.target.value = event.target === minInput ? maxInput.value : minInput.value;
    }

    if (event.target.value < 0) {
      event.target.value = 0;
    }
  }
}

// Mobile drawer controller
class OddaFacetDrawer {
  constructor() {
    this.drawer = document.querySelector('.odda-facets__drawer');
    if (!this.drawer) return;
    this.bindEvents();
  }

  bindEvents() {
    document.querySelectorAll('[data-odda-open-drawer]').forEach((btn) => {
      btn.addEventListener('click', () => this.open());
    });

    this.drawer.querySelectorAll('[data-odda-close-drawer]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    const overlay = this.drawer.querySelector('.odda-facets__drawer-overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    const firstFocusable = this.drawer.querySelector('button, input, select, a[href]');
    if (firstFocusable) firstFocusable.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.classList.add('is-closing');
    const content = this.drawer.querySelector('.odda-facets__drawer-content');
    const onEnd = () => {
      this.drawer.classList.remove('is-closing');
      document.body.style.overflow = '';
      content.removeEventListener('transitionend', onEnd);
    };
    content.addEventListener('transitionend', onEnd);
  }
}

// Close horizontal filter dropdowns when clicking outside
document.addEventListener('click', (e) => {
  if (!e.target.closest('.odda-facets__horizontal-group')) {
    document.querySelectorAll('.odda-facets__horizontal-group[open]').forEach((details) => {
      details.removeAttribute('open');
    });
  }
});

// Register custom elements
if (!customElements.get('odda-facet-filters-form')) {
  customElements.define('odda-facet-filters-form', OddaFacetFiltersForm);
}
if (!customElements.get('odda-facet-remove')) {
  customElements.define('odda-facet-remove', OddaFacetRemove);
}
if (!customElements.get('odda-price-range')) {
  customElements.define('odda-price-range', OddaPriceRange);
}

// Initialize drawer on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new OddaFacetDrawer());
} else {
  new OddaFacetDrawer();
}
